import { type QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInvalidateOnAdvancingActivity } from "advancing/activity/useInvalidateOnAdvancingActivity";
import { ButtonPrimary } from "components/Atoms/Button";
import DeleteDialog from "components/deleteDialog";
import { NoDataAvailableWarning } from "components/NoDataAvailableDiv";
import { Panel } from "components/panels";
import { useOpenSidePanel } from "components/SidePanel/SidePanel";
import Pagination from "pagination/components/pagination";
import { AdvancingUploadApi } from "queries/AdvancingUpload";
import React, { useState } from "react";
import { AdvancingUploadCard } from "./AdvancingUploadCard";
import { advancingUploadMutationKey, UploadAdvancingFilesSidePanel } from "./UploadAdvancingFilesSidePanel";

const perPage = 10;

interface Props {
  advancingSessionId: number
  instruction: server.dto.AdvancingSessionInstructionBase
  // Holds the session's file count shown on the tab
  sidePanelQueryKey: QueryKey
}

export const AdvancingFilesPanel = (props: Props) => {
  const queryClient = useQueryClient();
  const openSidePanel = useOpenSidePanel();
  const [page, setPage] = useState(1);
  const [deletingFile, setDeletingFile] = useState<server.dto.AdvancingFile>();

  const listInstruction = { ...props.instruction, page, perPage };
  const uploadsQuery = AdvancingUploadApi.Paginated.useQuery(listInstruction, { refetchOnWindowFocus: true });
  // Without the page, so every cached page is invalidated
  const uploadsQueryKey = ["AdvancingUpload.Paginated", props.instruction];

  useInvalidateOnAdvancingActivity(props.advancingSessionId, uploadsQueryKey, { skipWhileMutating: advancingUploadMutationKey });
  useInvalidateOnAdvancingActivity(props.advancingSessionId, props.sidePanelQueryKey, { skipWhileMutating: advancingUploadMutationKey });

  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: uploadsQueryKey }),
    queryClient.invalidateQueries({ queryKey: props.sidePanelQueryKey })
  ]);

  const deleteMutation = useMutation({
    mutationKey: advancingUploadMutationKey,
    mutationFn: (clientFileId: number) => window.CoreApi.AdvancingUpload.DeleteFile({ ...props.instruction, clientFileId }, { toastOnError: true }).then(r => r.data),
    onSuccess: async response => {
      if (response.hasErrors) return;
      await invalidate();
      setDeletingFile(undefined);
    }
  });

  const onUploadClick = () => {
    openSidePanel(
      <UploadAdvancingFilesSidePanel instruction={props.instruction} onUploaded={invalidate} />,
      "upload-advancing-files",
      "medium"
    );
  };

  const uploads = uploadsQuery.data?.data ?? [];

  return <>
    <Panel
      panelClassName="s1_panel"
      title={RESX.File.Files}
      button={<ButtonPrimary label={RESX.Buttons.Upload} onClick={onUploadClick} />}
      isLoading={uploadsQuery.isLoading}
      contentCssClass="flex-column gap-mini"
      pagination={<Pagination
        small
        onPaginate={setPage}
        paginationData={{
          page,
          perPage,
          total: uploadsQuery.data?.pagination.total ?? 0,
          pageCount: uploadsQuery.data?.pagination.pageCount ?? 0,
          maxPerPage: perPage
        }}
      />}
    >
      {uploads.map(upload => <AdvancingUploadCard
        key={upload.advancingUploadId}
        upload={upload}
        onDeleteFile={setDeletingFile}
      />)}
      {uploadsQuery.data && uploads.length === 0 && <NoDataAvailableWarning>There are no files</NoDataAvailableWarning>}
    </Panel>

    <DeleteDialog
      isOpen={deletingFile != null}
      message={deletingFile?.fileName}
      handleClose={() => setDeletingFile(undefined)}
      onDelete={() => deleteMutation.mutateAsync(deletingFile.clientFileId)}
    />
  </>;
};
